import { model as i18n } from '!/i18n'
import {
  epgProgramsModel,
  getMapKeyForProgram,
  type ProgramEpgInfo,
} from '$/epg/programs'
import { pipe } from '@mobily/ts-belt'
import { combine, createEvent, createStore, sample, type Store } from 'effector'
import { delay } from 'patronum'
import { formatProgram } from '~/entities/epg/programs/lib/upcoming'
import { selectEpgProgramModel } from '~/features/epg/programs/select'
import { model as clock } from '~/shared/clock'
import { F, T } from '~/shared/helpers'
import { DESELECT_AFTER_CLOSE_DELAY, type EpgSidebarInfo } from './index.h'
import { formatProgramToLocalizedView, formatToLocalizedView } from './l10n'
import { formatProgramToView, formatToView } from './lib'

export const init = createEvent()

// reset widget on sign out
export const reset = createEvent()

// reset widget on epg page left
export const left = createEvent()

// sidebar state (open/close)
export const open = createEvent()
export const close = createEvent()
export const $open = createStore(false) //
  .on(open, T)
  .on(close, F)

// sidebar showing info
export const setInfo = createEvent<EpgSidebarInfo | null>()
export const $info = createStore<EpgSidebarInfo | null>(null) //
  .on(setInfo, (_, info) => info)

// upcoming program epg info
export const $upcoming: Store<ProgramEpgInfo[] | null> = combine(
  {
    programs: epgProgramsModel.$upcoming,
    selected: selectEpgProgramModel.$program,
    i18n: i18n.$t,
  },
  ({ programs: [upcoming], selected }) =>
    selected == null //
      ? null
      : (upcoming
          .get(getMapKeyForProgram(selected.program))
          ?.map(formatProgram) ?? null)
)

// open sidebar on program select
sample({
  clock: selectEpgProgramModel.select,
  target: open,
})

// deselect program on sidebar close, with delay while sidebar is in closing animation
delay({
  source: close,
  timeout: DESELECT_AFTER_CLOSE_DELAY,
  target: selectEpgProgramModel.deselect,
})

// fetch program epg on sidebar open
sample({
  clock: open,
  source: selectEpgProgramModel.$program,
  filter: (selected) =>
    selected != null &&
    selected.program.epgId != null &&
    selected.program.title != null &&
    selected.program.startMs != null,

  fn: (selected) => selected!.program,
  target: epgProgramsModel.fetchUpcoming,
})

// update sidebar info on program select
sample({
  clock: [selectEpgProgramModel.$program, clock.$minutes],
  source: {
    i18n: i18n.$t,
    selected: selectEpgProgramModel.$program,
    now: clock.$minutes,
  },
  fn: ({ i18n: { t }, selected, now }) => {
    if (selected == null) return null
    return pipe(
      selected,
      formatProgramToView,
      formatProgramToLocalizedView({ t, now }),
      formatToView,
      formatToLocalizedView({ t })
    )
  },
  target: setInfo,
})

//
// left and reset logic
//

sample({
  clock: [left, reset],
  target: [$open.reinit, $info.reinit, selectEpgProgramModel.$selected.reinit],
})
